import { HashMap, TranslocoTranspiler, TranspileParams, isString } from '@jsverse/transloco';
import IntlMessageFormat from 'intl-messageformat';

export class IntlMessageFormatTranspiler implements TranslocoTranspiler {
  private readonly _messageCache = new Map<string, IntlMessageFormat>();

  transpile(params: TranspileParams<unknown>): string {
    const paramsValue = params.value;

    if (!isString(paramsValue)) {
      return paramsValue as string;
    }

    const value = this._convertPlaceholderSyntax(paramsValue);

    if (!this._hasPlaceholders(value)) {
      return value;
    }

    try {
      return this._format(value, params.params as HashMap, params.key);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('was not provided') &&
        value.includes('plural')
      ) {
        const missingParam = this._extractMissingParam(error);

        if (missingParam) {
          try {
            const augmentedParams = { ...(params.params || {}), [missingParam]: 2 };
            return this._format(value, augmentedParams, params.key);
          } catch (fallbackError) {
            console.error('Error formatting message with fallback:', fallbackError);
            return value;
          }
        }
      }

      console.error('Error formatting message:', error);
      return value;
    }
  }

  private _format(value: string, params: HashMap, key?: string): string {
    let processedValue = value;

    const hasRichTextFormatting = this._hasRichTextFormatting(value, params);

    if (!hasRichTextFormatting) {
      processedValue = this._escapeHtml(value);
    }

    const message = this._getOrCreateMessage(processedValue, key);
    const formatted = message.format(params);

    const result = hasRichTextFormatting ? formatted : this._unescapeHtml(formatted);

    return result ?? value;
  }

  private _extractMissingParam(error: Error): string | null {
    const match = error.message.match(/"([^"]+)"/);

    return match ? match[1] : null;
  }

  private _hasPlaceholders(value: string): boolean {
    return value.includes('{') && value.includes('}');
  }

  /**
   * Determines if the message uses rich text formatting by checking if HTML-like tags
   * in the message have corresponding function parameters.
   *
   * We extract tag names by removing the < and > brackets and check if matching
   * function parameters exist. This allows us to distinguish between rich text
   * formatting and literal HTML that should be escaped.
   *
   * The reason to do this is to support our current implementation
   */
  private _hasRichTextFormatting(value: string, params: HashMap): boolean {
    // Extract potential rich text tag names from the message
    const richTextTags = value.match(/<(\w+)>/g);
    if (!richTextTags) {
      return false;
    }

    // Check if any of these tag names exist as function parameters
    return richTextTags.some((tag) => {
      const tagName = tag.slice(1, -1); // Remove < and >
      return typeof params[tagName] === 'function';
    });
  }

  private _getCacheKey(value: string, key?: string): string {
    return key ? `${key}:${value}` : value;
  }

  private _getOrCreateMessage(value: string, key?: string): IntlMessageFormat {
    const cacheKey = this._getCacheKey(value, key);

    let message = this._messageCache.get(cacheKey);
    if (!message) {
      message = new IntlMessageFormat(value);
      this._messageCache.set(cacheKey, message);
    }

    return message;
  }

  private _escapeHtml(value: string): string {
    return value.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private _unescapeHtml(value: string): string {
    return value.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }

  /**
   * Convert "{{ foo }}" into ICU MessageFormat's "{foo}"
   */
  private _convertPlaceholderSyntax(value: string): string {
    return value.replace(/{{\s*(\w+)\s*}}/g, '{$1}');
  }
}
